/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { ICheckoutViewProps, ILineItem, ILineItemDeliveryGroup, ILineItems, IOrderSummary, IPickUpAtStore } from './krannich-c-checkout';
import { IEmailDelivery } from './components';
import { IInvoicePaymentSummary } from './components/get-invoice-payment-summary';
import { IKrannichCCheckoutResources } from './krannich-c-checkout.props.autogenerated';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { getDateWithOnePlus } from '../../utils/ATP/atpHelper';
import DatePicker, { registerLocale } from 'react-datepicker';
import enGb from 'date-fns/locale/en-GB';
import enUS from 'date-fns/locale/en-US';
import enAU from 'date-fns/locale/en-AU';
import { currencyFormat, dateFormatByLocale } from '../../utils/helper';
registerLocale('enGb', enGb);
registerLocale('enUS', enUS);
registerLocale('enAU', enAU);
interface IOrderSummaryExtended {
    orderSummary: IOrderSummary;
    promoCodeComponent: React.ReactNode;
    lessOrderMessage?: string;
    lessOrderMessageMX?: string;
    freightAndGSTMessage?: string;
    freightAndGSTMessageFromResources?: string;
    siteOun?: boolean;
    formatter?: any;
    subTotalToolTipMessage?: string;
    mexicoSiteFlag?: boolean;
    context?: any;
}

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummaryExtended> = ({
    orderSummary,
    promoCodeComponent,
    lessOrderMessage,
    freightAndGSTMessage,
    freightAndGSTMessageFromResources,
    siteOun,
    formatter,
    subTotalToolTipMessage,
    mexicoSiteFlag,
    lessOrderMessageMX,
    context
}) => {
    const { heading, lines } = orderSummary;
    const subTotalProps = lines?.subtotal as JSX.Element;
    const subtotal = subTotalProps.props.price;
    const shippingProps = lines?.shipping as JSX.Element;
    const totalBeforeTax = Number(subTotalProps?.props?.price) + Number(shippingProps?.props?.price);
    const taxProps = lines?.tax as JSX.Element;
    const tax = taxProps.props.price;
    const orderTotalProps = lines?.orderTotal as JSX.Element;
    const orderTotal = orderTotalProps.props.price;

    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {promoCodeComponent}
                {lines && (
                    <>
                        {!mexicoSiteFlag && subTotalProps?.props?.price < 1000
                            ? siteOun && (
                                  <div className='extra-charges-message-title'>
                                      <span className='extra-charges-message'>{lessOrderMessage}</span>
                                  </div>
                              )
                            : mexicoSiteFlag &&
                              subTotalProps?.props?.price < 500 && (
                                  <div className='extra-charges-message-title'>
                                      <span className='extra-charges-message'>{lessOrderMessageMX}</span>
                                  </div>
                              )}
                        <div className='price-with-tooltip'>
                            <p className='msc-order-summary__line-sub-total'>
                                <span className='msc-order-summary__label'>Subtotal</span>
                                <span className='msc-price msc-order-summary__value'>
                                    <span className='msc-price__actual' itemProp='price'>
                                        {currencyFormat(subtotal, context)}
                                    </span>
                                </span>
                            </p>
                            {!siteOun && subTotalToolTipMessage && (
                                <div className='tooltip-wrapper' tabIndex={0}>
                                    <span className='tooltip-icon' />
                                    <span className='tooltip-text tooltip-left'>{subTotalToolTipMessage}</span>
                                </div>
                            )}
                        </div>

                        {lines.shipping}
                        {
                            <div className='total_before_tax'>
                                <label className='total_before_tax_label'>Total before tax </label>
                                <span className='total_before_tax_value'>
                                    {/* {isNaN(totalBeforeTax) ? 'To be calculated' : `${'A$'}${totalBeforeTax}`} */}
                                    {isNaN(totalBeforeTax) ? 'To be calculated' : currencyFormat(Number(totalBeforeTax), context)}
                                </span>
                            </div>
                        }
                        {lines.otherCharge}
                        <p className='msc-order-summary__line-tax-amount'>
                            <span className='msc-order-summary__label'>Tax</span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual' itemProp='price'>
                                    {currencyFormat(tax, context)}
                                </span>
                            </span>
                        </p>
                        {lines.totalDiscounts}
                        {lines.loyalty}
                        {lines.customerAccount}
                        {lines.giftCard}
                        <p className='msc-order-summary__line-total'>
                            <span className='msc-order-summary__label'>Amount Due</span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual' itemProp='price'>
                                    {currencyFormat(orderTotal, context)}
                                </span>
                            </span>
                        </p>
                        <div className='checkout-summary-freight'>
                            <span>{freightAndGSTMessage ? freightAndGSTMessage : freightAndGSTMessageFromResources}</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        // lineItems,
        placeOrderButton,
        termsAndConditions,
        termsAndConditionsLine,
        // keepShoppingButton,
        promoCodeComponent,
        currentStep,
        checkoutExpressPaymentContainer,
        checkoutErrorRef,
        reqDate,
        calenderDaysOff
    } = props;
    const cart = props.data.checkout?.result?.checkoutCart?.cart;

    const attributes: AttributeValue[] = cart?.AttributeValues || [];
    const requestdeliverydateAttribute = props.context.app.config.requestdeliverydateAttribute || 'Request a delivery date';
    const reqAttr = attributes?.find(attr => attr.Name === requestdeliverydateAttribute)?.TextValue || undefined;
    const orderShippingdateAttribute = props.context.app.config.orderShippingdateAttribute || 'Estimated ship date';
    const shipDateAttr = attributes?.find(attr => attr.Name === orderShippingdateAttribute)?.TextValue || undefined;
    const formattedShipDate = new Date(shipDateAttr!).toLocaleDateString('en-GB') ?? shipDateAttr;
    const newSplitDateArr = formattedShipDate?.toString()?.split('/');
    let minDate = '';
    if (newSplitDateArr && newSplitDateArr?.length) {
        minDate = `${newSplitDateArr[2]}-${newSplitDateArr[1]}-${newSplitDateArr[0]}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const requestDeliveryDateMin = getDateWithOnePlus(minDate!);

    const [shipDateAttrState, shipDateAttrUseState] = React.useState<string | undefined>();
    const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);

    React.useEffect(() => {
        if (formattedShipDate) {
            shipDateAttrUseState(formattedShipDate);
        }
    }, [formattedShipDate]);

    React.useEffect(() => {
        if (reqDate !== undefined && reqDate && reqDate !== 'Invalid Date/Invalid Date/Invalid Date') {
            setSelectedDate(new Date(reqDate!));
        }
    }, [newSplitDateArr]);

    React.useEffect(() => {
        if (reqDate !== undefined && reqDate && reqDate !== 'Invalid Date/Invalid Date/Invalid Date') {
            setSelectedDate(new Date(reqDate!));
            //props.onChangeRequestDeliveryDate(new Date(reqDate!));
        }
    }, [reqDate]);

    // VSI CUSTOMIZATION for hanlding the clear date
    const handleChange = (date: Date | null) => {
        if (date instanceof Date && !isNaN(date.getTime())) {
            setSelectedDate(date);
            props.onChangeRequestDeliveryDate(date);
        } else {
            setSelectedDate(null);
        }
    };

    registerLocale('enGb', enGb);
    const dateObjects = calenderDaysOff?.map(dateString => new Date(dateString));
    const currency = props.context.request.channel?.Currency;
    const dateLocale = props.context.app.config.dateFormat;
    return (
        <>
            <Module {...checkoutProps} ref={checkoutErrorRef}>
                {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                {!hasSalesOrder &&
                    isPaymentVerificationRedirection &&
                    shouldEnableSinglePaymentAuthorizationCheckout &&
                    !alert &&
                    !loading && <Waiting className='msc-waiting-circular msc-waiting-lg' />}
                {!hasSalesOrder && (
                    <Node {...bodyProps}>
                        {loading}
                        {alert}
                        {canShow && (
                            <>
                                <Node {...mainProps}>
                                    {checkoutExpressPaymentContainer}
                                    {guidedForm}
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    <Node {...mainControlProps}>
                                        {_customFields(
                                            props,
                                            props.resources,
                                            props.onChange,
                                            props.onTextAreaChange,
                                            reqAttr,
                                            currentStep
                                        )}
                                        {/*keepShoppingButton*/}
                                    </Node>
                                    <div className='date-section-container'>
                                        <div className='date-section'>
                                            <div className='form-field col-lg-6'>
                                                <label>
                                                    {props?.context?.actionContext?.requestContext?.apiSettings?.oun !==
                                                    props.context.app.config.ounNumberForUsSite
                                                        ? props.resources.orderShipDate
                                                        : props.resources.orderShipDateUS}
                                                </label>
                                                {/* VSI CUSTOMIZATION | DATE FORMAT | START */}
                                                <label>
                                                    {formattedShipDate
                                                        ? dateLocale && currency
                                                            ? dateFormatByLocale(dateLocale, currency, formattedShipDate)
                                                            : formattedShipDate
                                                        : dateLocale && currency && shipDateAttrState
                                                        ? dateFormatByLocale(dateLocale, currency, shipDateAttrState)
                                                        : shipDateAttrState}
                                                </label>
                                                {/* VSI CUSTOMIZATION | DATE FORMAT | END */}
                                            </div>
                                            <div className='form-field col-lg-6 dd'>
                                                <label htmlFor='Request a delivery date'>{props.resources.requestedShippingDate}</label>
                                                <div>
                                                    <DatePicker
                                                        showIcon={true}
                                                        className='form-control input-md'
                                                        id='Request a delivery date'
                                                        name='Request a delivery date'
                                                        disabled={!!reqAttr}
                                                        onChange={handleChange}
                                                        selected={selectedDate}
                                                        minDate={
                                                            requestDeliveryDateMin && !isNaN(Date.parse(requestDeliveryDateMin))
                                                                ? new Date(requestDeliveryDateMin)
                                                                : null
                                                        }
                                                        placeholderText={`${dateLocale === 'en-US' ? 'MM-DD-YYYY' : 'DD-MM-YYYY'}`}
                                                        locale={`${dateLocale === 'en-US' ? 'enUS' : 'enAU'}`}
                                                        showTimeSelect={false}
                                                        excludeDates={dateObjects}
                                                        calendarStartDay={1}
                                                        dateFormat={`${dateLocale === 'en-US' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Node>
                                <div className='custom_side_control_div'>
                                    <Node {...sideProps}>
                                        {!hasInvoiceLine
                                            ? orderSummary && (
                                                  <OrderSummaryComponent
                                                      orderSummary={orderSummary}
                                                      promoCodeComponent={promoCodeComponent}
                                                      lessOrderMessage={props.config.lessOrderSummaryCheckoutInfoMessage}
                                                      lessOrderMessageMX={props.config.lessOrderSummaryCheckoutInfoMessageMX}
                                                      freightAndGSTMessage={props.config.freightAndGSTMessage}
                                                      freightAndGSTMessageFromResources={props.resources.freightAndGSTMessageResources}
                                                      siteOun={
                                                          props?.context?.actionContext?.requestContext?.apiSettings?.oun !==
                                                          props.context.app.config.ounNumberForUsSite
                                                      }
                                                      mexicoSiteFlag={
                                                          props?.context?.actionContext?.requestContext?.apiSettings?.oun ===
                                                          props.context.app.config.ounNumberForMexicoSite
                                                      }
                                                      formatter={props.context.cultureFormatter}
                                                      subTotalToolTipMessage={props.config.subtotalToolTipMessage}
                                                      context={props?.context}
                                                  />
                                              )
                                            : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                        <Node {...sideControlFirstProps}>
                                            <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                            {placeOrderButton}
                                        </Node>
                                        <Node {...sideControlSecondProps}>
                                            <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                        </Node>
                                    </Node>
                                </div>
                                {termsAndConditionsLine}
                                <div className='checkout-btn-desktop'>{placeOrderButton}</div>
                            </>
                        )}
                    </Node>
                )}
                {hasSalesOrder && orderConfirmation}
            </Module>
        </>
    );
};
const _customFields = (
    props: ICheckoutViewProps,
    resources: IKrannichCCheckoutResources,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    onTextAreaChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void,
    value?: string,
    currentStep?: number
) => (
    <>
        <label className='contact-info-label'>{resources.attributeSectionHeading}</label>
        <div className={`commission-customer-ref row  ${currentStep === 4 ? 'expanded-custom-info' : 'closed-custom-info'}`}>
            <div className='commision-customer-container'>
                <div className='form-field col-lg-6'>
                    <label htmlFor='customer-ref-id'>{resources.customerReference}</label>
                    <input
                        onChange={onChange}
                        type='text'
                        placeholder='Customer Reference ID'
                        className='form-control input-md'
                        id='customer-ref-id'
                        name='customerRefID'
                        maxLength={20}
                    />
                </div>
                {props.context.actionContext?.requestContext?.apiSettings?.oun !== props.context.app.config.ounNumberForUsSite && (
                    <div className='form-field col-lg-6'>
                        <label htmlFor='commission-id'>{resources.commision}</label>
                        <input
                            onChange={onChange}
                            type='text'
                            placeholder='Site Name'
                            className='form-control input-md'
                            id='commission-id'
                            name='commission'
                            maxLength={20}
                        />
                    </div>
                )}
            </div>
            <div className='form-field col-lg-12'>
                <label htmlFor='cutomer-comment-id'>{resources.customerComments}</label>
                <textarea
                    onChange={onTextAreaChange}
                    placeholder='Enter Comments...'
                    className='form-control input-md'
                    id='cutomer-comment-id'
                    name='customerComments'
                    maxLength={500}
                />
            </div>
        </div>
    </>
);

export default CheckoutView;
